import React, { useState } from 'react';
import type { AnalysisResults } from '../../types/frontend';

interface TwoDRitualRecommendationsSectionProps {
  ritualRecommendations: AnalysisResults['ritualRecommendations'];
  aggregateSentiment?: AnalysisResults['sentimentAnalysis'];
  selectedIds?: Set<string>;
  onToggleSelection?: (id: string) => void;
}

// Fallback ritual for when no good matches exist
const FALLBACK_RITUAL = {
  id: 'rit-kar-252-ii-58-iii-3-c',
  title: 'To remove the evil consequences of dreams',
  purpose: 'A simple, universal ritual suitable when dream meaning is uncertain or when no specific ritual strongly matches your dream.',
  conjurations: [
    'My dream was pleasant, my egirru good, good before Nanna and Utu! Upon the command of queen Ninlil, I have made a good omen—may it be for the good! May they remove what spell has been pronounced against me, may they remove what hate has done to me!',
    'Marduk, first-born son of the Abyss! It is for you to make things good and pleasant. Be conjured in the name of heaven, be conjured in the name of the netherworld!'
  ],
  ingredients: ['Soap', 'Water for handwashing'],
  procedure: 'Wash your hands with soap in bed before setting your foot on the floor. Then recite the conjuration three times, and you will be lucky.\n\nAlternative: Set your left foot on the right side of your bed, recite the conjuration three times, and rise.',
  reference: 'KAR 252 II: 58 - III: 3',
  sentiment: 'neutral',
  severity: '0',
};

export const TwoDRitualRecommendationsSection: React.FC<TwoDRitualRecommendationsSectionProps> = ({
  ritualRecommendations,
  selectedIds = new Set(),
  onToggleSelection,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true); // Collapsed by default
  
  // Filter out results below 50% threshold (less than 50% of best match)
  const filteredRituals = ritualRecommendations ? ritualRecommendations.filter(r => r.combined_score >= 0.5) : [];
  
  const topK = 3;
  let displayedRituals = filteredRituals.slice(0, topK);
  let isFallback = false;
  
  // If no good matches, offer the fallback ritual
  if (displayedRituals.length === 0 && ritualRecommendations && ritualRecommendations.length > 0) {
    displayedRituals = [{
      ritual: FALLBACK_RITUAL,
      semantic_score: 0,
      sentiment_compatibility: 0,
      combined_score: 0.75, // Show as reasonable match
      explanation: 'General protective ritual - suitable when dream meaning is uncertain',
    }];
    isFallback = true;
  }
  
  const hasRecommendations = displayedRituals.length > 0;

  return (
    <div className="result-section">
      <div
        className="section-header"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <span>Recommended rituals</span>
        <span className="chevron">{isCollapsed ? '▶' : '▼'}</span>
      </div>

      {!isCollapsed && (
        <>
          {!hasRecommendations ? (
            <div className="section-empty">
              <p>No ritual recommendations available.</p>
            </div>
          ) : (
            <div className="section-content">
              {isFallback && (
                <div
                  style={{
                    background: 'rgba(222, 184, 135, 0.15)',
                    border: '1px solid var(--dust)',
                    padding: 'var(--space-4)',
                    marginBottom: 'var(--space-4)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--soot)',
                    lineHeight: 1.5,
                  }}
                >
                  <strong>General recommendation:</strong> No specific rituals strongly matched your dream&apos;s content and sentiment. Below is a simple, universal protective ritual suitable for addressing uncertain or forgotten dream meanings.
                </div>
              )}
              {/* Ritual Cards */}
              {displayedRituals.map((recommendation) => {
                const ritual = recommendation.ritual;
                
                const getRelevanceLabel = (score: number, isFallbackRitual: boolean) => {
                  if (isFallbackRitual) return 'General recommendation';
                  const pct = Math.round(score * 100);
                  return pct === 100 ? 'Best match' : `${pct}% relevance`;
                };
                
                const isSelected = selectedIds.has(ritual.id);
                
                return (
                  <article
                    key={ritual.id}
                    className="entity-card"
                    style={{
                      background: 'var(--papyrus-white)',
                      padding: 'var(--space-6)',
                      marginBottom: 'var(--space-4)',
                      position: 'relative',
                      transform: isSelected ? 'translateX(12px)' : 'translateX(0)',
                      transition: 'transform 0.2s ease',
                      cursor: onToggleSelection ? 'pointer' : 'default',
                    }}
                    onClick={() => onToggleSelection?.(ritual.id)}
                    title={onToggleSelection ? (isSelected ? 'Remove from interpretation' : 'Add to interpretation') : undefined}
                  >
                    {/* Selection tab on left edge */}
                    {onToggleSelection && (
                      <div
                        style={{
                          position: 'absolute',
                          left: '-8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '8px',
                          height: '60%',
                          background: isSelected ? 'var(--lapis-lazuli)' : 'var(--stone-dark)',
                          borderRadius: '4px 0 0 4px',
                          transition: 'background 0.2s ease',
                          opacity: isSelected ? 1 : 0.3,
                        }}
                      />
                    )}
                    {/* Title with ranking number */}
                    <div
                      className="ritual-card-header"
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 'var(--space-4)',
                        gap: 'var(--space-3)',
                        flexWrap: 'wrap',
                      }}
                    >
                      <h4
                        style={{
                          fontSize: 'var(--text-lg)',
                          fontWeight: 500,
                          color: 'var(--charcoal)',
                          margin: 0,
                          flex: '1 1 auto',
                          minWidth: '200px',
                        }}
                      >
                        {ritual.title}
                      </h4>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: 'var(--space-1)',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 'var(--text-sm)',
                            fontWeight: 500,
                            color: 'var(--lapis-lazuli)',
                            fontFamily: 'var(--font-mono)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {getRelevanceLabel(recommendation.combined_score, isFallback)}
                        </span>
                      </div>
                    </div>

                    {/* Purpose */}
                    <p
                      style={{
                        fontSize: 'var(--text-base)',
                        color: 'var(--soot)',
                        lineHeight: 1.6,
                        marginBottom: 'var(--space-2)',
                      }}
                    >
                      {ritual.purpose}
                    </p>

                    {/* Ritual severity interpretation */}
                    {ritual.severity !== undefined && (
                      <div
                        style={{
                          fontSize: 'var(--text-xs)',
                          color: 'var(--dust)',
                          fontStyle: 'italic',
                          marginBottom: 'var(--space-4)',
                        }}
                      >
                        {(() => {
                          const severity = parseFloat(ritual.severity);
                          
                          if (severity < -2) return 'For extremely bad dreams';
                          if (severity < -1) return 'For very bad dreams';
                          if (severity < -0.5) return 'For moderately bad dreams';
                          if (severity > 2) return 'For extremely good dreams';
                          if (severity > 1) return 'For very good dreams';
                          if (severity > 0.5) return 'For moderately good dreams';
                          return 'For neutral dreams';
                        })()}
                      </div>
                    )}

                    {/* Ingredients */}
                    {ritual.ingredients && ritual.ingredients.length > 0 && (
                      <div style={{ marginBottom: 'var(--space-4)' }}>
                        <h5
                          style={{
                            fontSize: 'var(--text-xs)',
                            fontWeight: 500,
                            color: 'var(--dust)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: 'var(--space-2)',
                          }}
                        >
                          Ingredients
                        </h5>
                        <p
                          style={{
                            fontSize: 'var(--text-sm)',
                            color: 'var(--soot)',
                            lineHeight: 1.6,
                          }}
                        >
                          {ritual.ingredients.map((ingredient: string, i: number) => (
                            <React.Fragment key={i}>
                              <span>{ingredient}</span>
                              {i < ritual.ingredients.length - 1 && <span>, </span>}
                            </React.Fragment>
                          ))}
                        </p>
                      </div>
                    )}

                    {/* Procedure */}
                    <div style={{ marginBottom: 'var(--space-4)' }}>
                      <h5
                        style={{
                          fontSize: 'var(--text-xs)',
                          fontWeight: 500,
                          color: 'var(--dust)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: 'var(--space-2)',
                        }}
                      >
                        Procedure
                      </h5>
                      <p
                        style={{
                          fontSize: 'var(--text-sm)',
                          color: 'var(--charcoal)',
                          lineHeight: 1.7,
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {ritual.procedure}
                      </p>
                    </div>

                    {/* Conjurations */}
                    {ritual.conjurations && ritual.conjurations.length > 0 && (
                      <div
                        style={{
                          paddingLeft: 'var(--space-4)',
                          borderLeft: '2px solid var(--carnelian)',
                          marginBottom: 'var(--space-4)',
                        }}
                      >
                        {ritual.conjurations.map((conjuration: string, i: number) => (
                          <p
                            key={i}
                            style={{
                              fontSize: 'var(--text-sm)',
                              color: 'var(--soot)',
                              fontStyle: 'italic',
                              lineHeight: 1.7,
                              marginBottom:
                                i < ritual.conjurations.length - 1 ? 'var(--space-2)' : 0,
                            }}
                          >
                            &ldquo;{conjuration}&rdquo;
                          </p>
                        ))}
                      </div>
                    )}

                    {/* Reference */}
                    {ritual.reference && (
                      <div
                        style={{
                          paddingTop: 'var(--space-3)',
                          borderTop: '1px solid var(--stone-light)',
                          fontSize: 'var(--text-xs)',
                          color: 'var(--dust)',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        {ritual.reference}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};
